import React from 'react';
import { Pressable } from 'react-native';

import { Text, VStack, theme } from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import {
  type WaitlistTimePreference,
  getOfferedTimePreferences,
  toggleTimePreference,
} from '@/utils/waitlistTimePreferences';

import { styles } from './styles';
import type { WaitlistTimePreferencePickerProps } from './types';

type RowKey = 'anytime' | WaitlistTimePreference;

export function WaitlistTimePreferencePicker(
  props: WaitlistTimePreferencePickerProps,
) {
  const {
    selected,
    showAfterHours,
    onChange,
    disabled = false,
    testID = 'waitlist-time-preference-picker',
  } = props;

  const { t } = useScreenTranslations('WaitlistTimePreferencePicker');
  const offered = getOfferedTimePreferences(showAfterHours);

  const renderRow = (
    key: RowKey,
    isChecked: boolean,
    onPress: () => void,
    range?: string,
  ) => (
    <Pressable
      key={key}
      testID={`${testID}-${key}`}
      onPress={onPress}
      disabled={disabled}
      style={[styles.row, isChecked && styles.rowSelected]}
      accessibilityRole="checkbox"
      aria-checked={isChecked}
      aria-disabled={disabled || undefined}
      accessibilityLabel={t(`accessibility.${key}`)}
      accessibilityHint={t(
        key === 'anytime'
          ? 'accessibility.anytimeHint'
          : 'accessibility.rowHint',
      )}
    >
      <Text
        variant="mStrong"
        color={isChecked ? 'textPrimaryInverse' : 'textPrimary'}
        style={styles.label}
      >
        {t(key)}
      </Text>
      {range && (
        <Text
          variant="m"
          color={isChecked ? 'textPrimaryInverse' : 'textSecondary'}
        >
          {range}
        </Text>
      )}
    </Pressable>
  );

  return (
    <VStack space={theme.spacing[3]} testID={testID}>
      <Text
        variant="l"
        color="textPrimary"
        accessibilityRole="header"
        style={styles.title}
      >
        {t('title')}
      </Text>
      {renderRow('anytime', selected.length === 0, () => onChange([]))}
      {offered.map((preference) =>
        renderRow(
          preference,
          selected.includes(preference),
          () => onChange(toggleTimePreference(selected, preference, offered)),
          t(`range.${preference}`),
        ),
      )}
    </VStack>
  );
}
